<template>
  <!-- prettier-ignore -->
  <div
    class="gender"
    :class="classes">
    <div class="gender-element">
      <template v-if="!isUnborn">
        <label
          class="gender-item"
          :class="{ 'gender-item--selected': isFemale }"
          @click="select('FEMALE')"
        >
          <input
            type="radio"
            name="gender"
            value="FEMALE"
            :checked="isFemale"
          />
          <ovp-icon
            art="solid"
            name="person-dress-simple"
          />
          <span
            class="gender-item__text"
            v-text="customLabelFemale ? customLabelFemale : $t('person.gender.female')"
          />
        </label>

        <label
          class="gender-item"
          :class="{ 'gender-item--selected': isMale }"
          @click="select( 'MALE')">
          <input
            type="radio"
            name="gender"
            value="MALE"
            :checked="isMale"
          />
          <ovp-icon
            art="solid"
            name="person-simple"
          />
          <span
            class="gender-item__text"
            v-text="customLabelMale? customLabelMale : $t('person.gender.male')"
          />
        </label>
      </template>

      <template v-else-if="autoSelection && isUnborn">
        <label
          class="gender-item"
          :class="{
            'gender-item--unborn': true,
            'gender-item--selected': isUnborn,
          }"
          @click="select('UNBORN')"
        >
          <input
            type="radio"
            name="gender"
            value="UNBORN"
            :checked="isUnborn"
          />
          <ovp-icon
            art="solid"
            name="baby-carriage"
          />
          <span
            class="gender-item__text"
            v-text="customLabelUnborn? customLabelUnborn : $t('person.gender.unborn')"
          />
        </label>
      </template>
    </div>
    <div
      class="gender__hint"
      v-text="autoHint"
    />
  </div>
</template>

<script>
import { GENDER_FEMALE, GENDER_MALE, GENDER_UNBORN } from '@/config/constants'

export default {
  name: 'OvpGender',

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    // show male / female by default
    age: {
      type: Number,
      default: 18,
    },
    autoSelection: {
      type: Boolean,
      default: true,
    },

    customLabelFemale: {
      type: String,
      default: '',
    },

    customLabelMale: {
      type: String,
      default: '',
    },

    customLabelUnborn: {
      type: String,
      default: '',
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    theme: {
      type: String,
      default: 'transparent',
    },

    validate: {
      type: Object,
      default: () => {},
    },

    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      form: {
        gender: this.value,
      },
    }
  },

  watch: {
    age() {
      this.$nextTick(() => {
        this.autoSelect()
      })
    },

    value(gender) {
      this.form.gender = gender
    },
  },

  computed: {
    autoHint() {
      const errors = Object.keys(this.validate?.$params ?? {}).filter(error => !this.validate[error])

      if (this.invalid && errors.length > 0) {
        return this.$t(`form.error.${errors[0]}`)
      } else {
        return this.hint
      }
    },

    classes() {
      return {
        'gender--invalid': this.invalid,
        'gender--readonly': this.readonly,
        [`gender--${this.theme}`]: true,
      }
    },

    invalid() {
      return this.validate?.$invalid && this.validate?.$error
    },

    isMale() {
      return this.form.gender === GENDER_MALE
    },

    isFemale() {
      return this.form.gender === GENDER_FEMALE
    },

    isUnborn() {
      return this.form.gender === GENDER_UNBORN
    },
  },

  methods: {
    autoSelect() {
      if (this.age < 0) {
        this.form.gender = GENDER_UNBORN
        this.$emit('update', this.form.gender)
      } else if (this.age >= 0 && this.form.gender === GENDER_UNBORN) {
        this.form.gender = null
        this.$emit('update', this.form.gender)
      }
    },

    select(gender) {
      this.form.gender = gender
      this.validate?.$touch()
      this.$emit('update', gender)
    },
  },
}
</script>

<style name="mobile" scoped>
.gender {
  height: 46px;
  min-width: 180px;

  &-element {
    display: grid;
    grid-gap: 8px;
    grid-template-columns: 1fr 1fr;

    align-items: center;
    height: 46px;

    input {
      position: absolute;
      display: inline-block;
      opacity: 0;
      height: 1px;
      width: 1px;
    }
  }

  &-item {
    color: var(--c-black);
    padding: 0 0 0 8px;
    cursor: pointer;

    .icon {
      font-size: 25px;
      color: inherit;
    }

    &__text {
      margin: 0 0 0 8px;
    }

    &:hover,
    &--selected {
      color: var(--c-original-green);
    }

    &--selected {
      cursor: default;
    }

    &--unborn {
      grid-column-start: 1;
      grid-column-end: 3;
    }
  }

  &__hint {
    font-size: 15px;
    position: relative;
    padding: 0 0 0 10px;
    margin-top: -5px;
  }

  &--invalid {
    .gender__hint {
      color: var(--c-error);
    }
  }

  &--readonly {
    pointer-events: none;
  }

  &--transparent {
    background-color: transparent;
  }

  &--light {
    background-color: var(--c-white);
  }
  &--dark {
    background-color: var(--c-warm-gray);
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .gender {
  }
}
</style>
